// Verification image — the agent rendered a Luma image grounded in
// the FULL brand graph (not just a single PhotographyStyle anchor).
// The card shows a per-category breakdown of which nodes shaped the
// result, with an expandable list of the actual labels so the user
// can see exactly what fed the prompt. Hovering the breakdown also
// pushes the contributing nodes into the atlas spotlight so the user
// can see them light up.

import {
  ChevronDown,
  ChevronUp,
  Image as ImageIcon,
  Loader2,
  Sparkles,
} from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import type { GraphSnapshot, GraphVerificationArtifact } from "@/lib/types";
import { cn } from "@/lib/utils";

interface Props {
  verification: GraphVerificationArtifact;
  /** Snapshot — used to resolve contributing_node_ids → display labels
   *  for the expandable list. */
  snapshot: GraphSnapshot | null;
  /** Fires when the user clicks "Looks right" — pre-fills the composer
   * with a confirmation message. */
  onLooksRight: (verification: GraphVerificationArtifact) => void;
  /** Fires when the user clicks "Doesn't match" — pre-fills the
   * composer with a revision request. */
  onRevise: (verification: GraphVerificationArtifact) => void;
  /** Push contributing node ids into the spotlight focus while the
   * user is hovering the breakdown (so they SEE which nodes shaped
   * this image on the atlas). Cleared on mouse-leave. */
  onHoverBreakdown?: (nodeIds: string[]) => void;
}

// Pretty labels for breakdown keys. The server sends lower-case
// category names; we surface them title-cased to the user.
const BREAKDOWN_LABEL: Record<string, string> = {
  photography: "photography",
  palette: "palette",
  voice: "voice",
  values: "values",
  rejected: "rejected",
  audience: "audience",
};

export function VerificationImageCard({
  verification: v,
  snapshot,
  onLooksRight,
  onRevise,
  onHoverBreakdown,
}: Props) {
  const [promptOpen, setPromptOpen] = useState(false);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  // Defensive defaults — verification artifacts persisted from before
  // 3.5e shipped don't have these fields. Crashing the renderer over
  // a cached artifact would lose the entire chat history.
  const contributingIds = v.contributing_node_ids ?? [];
  const breakdown = v.breakdown ?? {};

  // Resolve contributing node ids → labels via the snapshot. Skip ids
  // we can't find (node may have been edited since generation).
  const contributingNodes = (() => {
    if (!snapshot) return [];
    const all = Object.values(snapshot.nodes_by_category ?? {}).flat();
    const byId = new Map(all.map((n) => [n.id, n] as const));
    return contributingIds
      .map((id) => byId.get(id))
      .filter((n) => n !== undefined);
  })();

  const totalContrib = contributingIds.length;
  const breakdownItems = Object.entries(breakdown)
    .filter(([, n]) => n > 0)
    .map(([k, n]) => ({
      key: k,
      label: BREAKDOWN_LABEL[k] || k,
      count: n,
    }));

  const headline =
    v.node_display_label && v.primary_node_id
      ? v.node_display_label
      : "Brand portrait";

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      {/* Header */}
      <div className="px-4 pt-3 pb-2 flex items-start gap-3">
        <div className="mt-0.5 h-7 w-7 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-700">
          <Sparkles className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Verification image
          </div>
          <div className="text-sm font-medium text-foreground mt-0.5 truncate">
            {headline}
          </div>
        </div>
      </div>

      {/* Hero image */}
      <div className="relative bg-muted/30 aspect-[3/2] flex items-center justify-center overflow-hidden">
        {!imgLoaded && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground text-xs">
            <Loader2 className="h-4 w-4 animate-spin mb-1.5" />
            Loading image…
          </div>
        )}
        <img
          src={v.image_url}
          alt={headline}
          onLoad={() => setImgLoaded(true)}
          className={cn(
            "w-full h-full object-cover transition-opacity duration-300",
            imgLoaded ? "opacity-100" : "opacity-0"
          )}
        />
      </div>

      {/* Breakdown — the new headline feature. Hovering it pushes
          contributing nodes into the spotlight so the user SEES them
          light up on the atlas. Clickable to expand the full list. */}
      {breakdownItems.length > 0 && (
        <div
          className="px-4 py-2 border-t border-border"
          onMouseEnter={() => onHoverBreakdown?.(contributingIds)}
          onMouseLeave={() => onHoverBreakdown?.([])}
        >
          <button
            type="button"
            onClick={() => setBreakdownOpen((o) => !o)}
            className="w-full text-left flex items-start gap-2 hover:bg-accent/30 -mx-1 px-1 py-1 rounded transition-colors"
          >
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                Based on {totalContrib} node{totalContrib === 1 ? "" : "s"} ·{" "}
                hover to highlight on atlas
              </div>
              <div className="flex flex-wrap gap-1.5">
                {breakdownItems.map((item) => (
                  <span
                    key={item.key}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted/60 border border-border text-[11px] text-foreground"
                  >
                    <span className="font-medium tabular-nums">{item.count}</span>
                    <span className="text-muted-foreground">{item.label}</span>
                  </span>
                ))}
              </div>
            </div>
            <div className="mt-1.5 text-muted-foreground shrink-0">
              {breakdownOpen ? (
                <ChevronUp className="h-3.5 w-3.5" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5" />
              )}
            </div>
          </button>

          {/* Expanded list of contributing nodes */}
          {breakdownOpen && (
            <div className="mt-2 grid grid-cols-1 gap-1">
              {contributingNodes.length === 0 ? (
                <div className="text-[11px] text-muted-foreground italic">
                  Snapshot still loading — the per-node labels will appear
                  once the atlas refreshes.
                </div>
              ) : (
                contributingNodes.map((n) => (
                  <div
                    key={n!.id}
                    className="flex items-start gap-2 text-xs text-foreground py-0.5"
                    onMouseEnter={() => onHoverBreakdown?.([n!.id])}
                    onMouseLeave={() => onHoverBreakdown?.(contributingIds)}
                  >
                    <span
                      className="mt-1 h-1.5 w-1.5 rounded-full shrink-0"
                      style={{ background: bulletFor(n!.category) }}
                    />
                    <span className="flex-1 min-w-0">
                      <span>{n!.display_label || n!.content}</span>
                      {v.primary_node_id === n!.id && (
                        <span className="ml-1.5 text-[10px] text-amber-700 font-semibold">
                          ★ PRIMARY
                        </span>
                      )}
                    </span>
                    <span className="text-[10px] text-muted-foreground uppercase tracking-wide shrink-0">
                      {n!.category}
                    </span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Refs used */}
      {v.ref_ids.length > 0 && (
        <div className="px-4 py-2 border-t border-border flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <ImageIcon className="h-3 w-3" />
          Used {v.ref_ids.length} reference image
          {v.ref_ids.length === 1 ? "" : "s"} as conditioning
        </div>
      )}

      {/* Creative prompt (collapsible) */}
      <div className="border-t border-border">
        <button
          type="button"
          onClick={() => setPromptOpen((v) => !v)}
          className="w-full px-4 py-2 text-left text-[11px] text-muted-foreground flex items-center gap-1.5 hover:bg-accent/40"
        >
          {promptOpen ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )}
          <span>{promptOpen ? "Hide prompt" : "Show creative prompt"}</span>
          <span className="text-muted-foreground/60 ml-1">· {v.aspect_ratio}</span>
        </button>
        {promptOpen && (
          <div className="px-4 pb-3 text-xs text-muted-foreground whitespace-pre-wrap font-mono leading-relaxed">
            {v.prompt}
            {v.composed_prompt && v.composed_prompt !== v.prompt && (
              <div className="mt-2 pt-2 border-t border-border/60 opacity-80">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground/80 mb-1">
                  Server-appended brand context
                </div>
                {v.composed_prompt.slice(v.prompt.length).trim()}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Reaction buttons */}
      <div className="px-4 py-3 border-t border-border bg-muted/30 flex items-center gap-2 flex-wrap">
        <Button
          size="sm"
          onClick={() => onLooksRight(v)}
          className="bg-slate-900 text-white hover:bg-slate-800"
        >
          Looks right
        </Button>
        <Button size="sm" variant="outline" onClick={() => onRevise(v)}>
          Doesn't match
        </Button>
      </div>
    </div>
  );
}

/** Match the atlas's per-category bullet colors. Mirror of BrandAtlas's
 *  hue table — duplicated here so the card doesn't need to import
 *  the atlas internals just for the bullet color. */
function bulletFor(category: string): string {
  switch (category) {
    case "ToneOfVoice":
      return "oklch(0.62 0.14 28)";
    case "Value":
    case "RejectedValue":
      return "oklch(0.62 0.14 260)";
    case "Audience":
      return "oklch(0.62 0.14 200)";
    case "PhotographyStyle":
      return "oklch(0.62 0.14 140)";
    case "ColorPalette":
    case "Color":
      return "oklch(0.62 0.14 0)";
    case "Note":
      return "oklch(0.62 0.14 50)";
    case "Ritual":
      return "oklch(0.62 0.14 310)";
    default:
      return "oklch(0.62 0.14 280)";
  }
}
